import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Empty,
  Modal,
  Select,
  Table,
  TextArea,
  Toast,
  Typography,
} from "@douyinfe/semi-ui";
import { AiActionTrack, AiCoachApi } from "../../../api/aiCoach";
import { STATUS_COLOR_MAP, STATUS_OPTIONS } from "../constants";
import { radius, sp } from "../styles";

const { Title, Text } = Typography;

interface Props {
  reportId: number;
  /** 外部触发刷新（例如接受新行动后）。每次 +1 即触发一次 reload */
  refreshKey?: number;
  /** 数据加载完成后回传已接受的 adjustmentIndex 集合，给 AdjustmentCards 用作禁用态 */
  onAcceptedIndexes?: (indexes: number[]) => void;
  onChanged: () => void;
}

const STATUS_DOT: Record<string, string> = {
  grey: "var(--semi-color-grey)",
  blue: "var(--semi-color-primary)",
  green: "var(--semi-color-success)",
  red: "var(--semi-color-danger)",
};

const ActionBoard: React.FC<Props> = ({ reportId, refreshKey, onAcceptedIndexes, onChanged }) => {
  const [rows, setRows] = useState<AiActionTrack[]>([]);
  const [noteModalFor, setNoteModalFor] = useState<number | null>(null);
  const [noteText, setNoteText] = useState("");
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await AiCoachApi.listActionsByReport(reportId);
      const list = r.data ?? [];
      setRows(list);
      if (onAcceptedIndexes) {
        onAcceptedIndexes(list.map((a) => a.adjustmentIndex));
      }
    } finally {
      setLoading(false);
    }
  }, [reportId, onAcceptedIndexes]);

  useEffect(() => {
    reload();
  }, [reload, refreshKey]);

  const changeStatus = async (id: number, status: string) => {
    if (status === "done") {
      setNoteModalFor(id);
      setNoteText("");
      return;
    }
    try {
      await AiCoachApi.updateAction(id, { status });
      Toast.success("状态已更新");
      reload();
      onChanged();
    } catch {
      Toast.error("更新失败");
    }
  };

  const submitNote = async () => {
    if (noteModalFor === null) return;
    try {
      await AiCoachApi.updateAction(noteModalFor, {
        status: "done",
        completionNote: noteText,
      });
      Toast.success("已标记完成");
      setNoteModalFor(null);
      reload();
      onChanged();
    } catch {
      Toast.error("更新失败");
    }
  };

  const columns = [
    {
      title: "#",
      dataIndex: "adjustmentIndex",
      width: 56,
      render: (i: number) => (
        <Text strong style={{ color: "var(--semi-color-primary)" }}>
          #{i + 1}
        </Text>
      ),
    },
    { title: "执行计划", dataIndex: "plannedAction" },
    {
      title: "状态",
      width: 170,
      render: (_: unknown, row: AiActionTrack) => {
        const color = STATUS_COLOR_MAP[row.status] ?? "grey";
        return (
          <div style={{ display: "flex", alignItems: "center", gap: sp.sm }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: STATUS_DOT[color],
                flexShrink: 0,
              }}
            />
            <Select
              value={row.status}
              size="small"
              style={{ width: "100%" }}
              onChange={(v) => changeStatus(row.id, v as string)}
            >
              {STATUS_OPTIONS.map((o) => (
                <Select.Option key={o.value} value={o.value}>
                  {o.label}
                </Select.Option>
              ))}
            </Select>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Card
        style={{ marginBottom: sp.lg, borderRadius: radius.md }}
        bodyStyle={{ padding: sp.lg }}
      >
        <div style={{ marginBottom: sp.md }}>
          <Title heading={5} style={{ margin: 0 }}>
            本周已接受行动
          </Title>
          {rows.length > 0 && (
            <Text type="tertiary" size="small" style={{ marginTop: sp.xs, display: "block" }}>
              共 {rows.length} 项，已完成 {rows.filter((r) => r.status === "done").length} 项
            </Text>
          )}
        </div>

        {rows.length === 0 ? (
          <Empty description="尚未接受任何行动" />
        ) : (
          <Table
            loading={loading}
            columns={columns as any}
            dataSource={rows}
            rowKey="id"
            pagination={false}
          />
        )}
      </Card>

      <Modal
        title="完成备注"
        visible={noteModalFor !== null}
        onCancel={() => setNoteModalFor(null)}
        footer={
          <>
            <Button onClick={() => setNoteModalFor(null)}>取消</Button>
            <Button theme="solid" type="primary" onClick={submitNote}>
              确认完成
            </Button>
          </>
        }
      >
        <TextArea
          value={noteText}
          onChange={setNoteText}
          autosize={{ minRows: 3 }}
          placeholder="简述执行结果（可选）"
        />
      </Modal>
    </>
  );
};

export default ActionBoard;
